/* eslint-disable @nx/enforce-module-boundaries */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Modal } from '@mui/material';
import link from 'packages/novous_web/src/assets/icons/Link.svg';
import InputField from './InputField';
import CustomIconButton from './CustomIconButton';
import { API_ENDPOINTS } from '../../api/endpoints';
import useAxios from '../../api/useAxios';
import AlertToast from './Alert';
import { useAuth } from '../../authContext/useAuth';
import useApiError from '../../api/useApiError';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  heading?: string;
  shareLink: any;
  subheading?: string;
  tripId?: string;
  fleetId?: string;
  submitUrl?: string;
  buildPayload?: (email: string[]) => any;
  context?: 'tripTable' | 'tripDetails'; // Add context prop
  vehicleId?: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  heading = 'Share',
  subheading = 'Enter an email address to share',
  shareLink,
  tripId,
  fleetId,
  submitUrl,
  buildPayload,
  context = 'tripDetails', // Default context
  vehicleId,
}) => {
  const [email, setEmail] = useState('');
  const [userEmailValid, setUserEmailValid] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const { user } = useAuth();

  const defaultSubmitUrl =
    context === 'tripTable'
      ? API_ENDPOINTS.dashboard.SHARE_TRIP_DETAILS
      : API_ENDPOINTS.alarm.VEHICLE_ALERT_EMAIL;

  const {
    data: shareTripData,
    error: shareTripError,
    loading: shareTripLoading,
    fetchData: fetchShareTripData,
  } = useApiError<any>(submitUrl || defaultSubmitUrl, 'POST');

  const validateEmail = (email: string): boolean => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const validateMultipleEmails = (
    emailString: string,
  ): { isValid: boolean; emails: string[]; errorMessage?: string } => {
    if (!emailString.trim()) {
      return { isValid: false, emails: [], errorMessage: 'Please enter at least one email address' };
    }

    const emails = emailString
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    if (emails.length === 0) {
      return { isValid: false, emails: [], errorMessage: 'Please enter at least one email address' };
    }

    for (const email of emails) {
      if (!validateEmail(email)) {
        return { isValid: false, emails: [], errorMessage: `Invalid email format: ${email}` };
      }
    }

    return { isValid: true, emails };
  };

  useEffect(() => {
    const emailValidation = validateMultipleEmails(email);
    setUserEmailValid(emailValidation.isValid);
    setEmailError(emailValidation.errorMessage || '');
  }, [email]);

  const handleSubmit = async () => {
    const emailValidation = validateMultipleEmails(email);
    if (!emailValidation.isValid) {
      setToast({
        open: true,
        message: emailValidation.errorMessage || 'Please enter valid email addresses',
        type: 'error',
      });
      return;
    }

    setIsSendingEmail(true);
    try {
      let payload;

      if (buildPayload) {
        payload = buildPayload(emailValidation.emails);
      } else if (context === 'tripTable') {
        // Payload for trip table sharing
        payload = {
          emailId: emailValidation.emails[0],
          tripId: tripId,
          fleetId: fleetId || user?.customer_id,
        };
      } else {
        // Payload for trip details sharing (adjust as needed)
        payload = {
          emailId: emailValidation.emails[0],
          // Add other necessary fields for trip details sharing
          deviceId: shareLink?.deviceId,
          warningSortKey: shareLink?.warningSortKey,
          vehicleId: vehicleId,
        };
      }

      const response = await fetchShareTripData(payload);

      if (response?.status === 201 || response?.status === 200) {
        setToast({
          open: true,
          message:
            response?.data?.message ||
            `Shared successfully with ${emailValidation.emails.length} recipient${
              emailValidation.emails.length > 1 ? 's' : ''
            }`,
          type: 'success',
        });
        setEmail('');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setToast({
          open: true,
          message: response?.error || 'Failed to share',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setToast({
        open: true,
        message: 'An error occurred while sharing',
        type: 'error',
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleClose = () => {
    if (shareTripLoading) {
      return;
    }
    setEmail('');
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Typography variant="h6">{heading}</Typography>
            <CustomIconButton
              text={`Copy Link`}
              icon={<img src={link} alt="link" width={24} height={24} />}
              background="transparent"
              outline={false}
              padding="0px"
              size={16}
              variant="outlined"
              onClick={() => {
                navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => {
                    console.log('Link copied to clipboard!');
                  })
                  .catch((err) => {
                    console.error('Failed to copy link: ', err);
                  });
              }}
            />
          </Box>
          <Typography variant="button" fontWeight={400} className="mb-1" color="secondary">
            {subheading}
          </Typography>
          <div className="mt-4">
            <InputField
              label="Email Addresses"
              name="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="Enter email addresses (user1@example.com, user2@example.com)"
              status={email.length === 0 ? undefined : userEmailValid ? 'success' : 'error'}
              isDisabled={false}
            />
          </div>
          <div className="flex w-full mt-8 justify-between">
            <CustomIconButton
              text={`Cancel`}
              background="transparent"
              outline={true}
              padding="0px"
              size={16}
              variant="outlined"
              onClick={handleClose}
              isDisabled={shareTripLoading}
            />
            <CustomIconButton
              text={shareTripLoading ? 'Sending...' : 'Send'}
              padding="0px"
              outline={false}
              size={16}
              isDisabled={!userEmailValid || isSendingEmail}
              variant="contained"
              onClick={handleSubmit}
            />
          </div>
        </Box>
      </Modal>
      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast({ ...toast, open: false })}
        type={toast.type as any}
      />
    </>
  );
};

export default ShareModal;
